import * as React from 'react';

import { Button } from '@fluentui/react';

import { dataUniqueId, InputMethod } from '@1js/acui-common';
import { AppContext } from '@1js/acui-context';
import { checkExperiment, ribbonExperiments } from '@1js/acui-experiments';
import { iconNameToIconProps } from '@1js/acui-icons';
import { defaultKeytipExecute } from '@1js/acui-keytip';
import { AppTooltipHost, getTooltipText } from '@1js/acui-tooltip';

import { deriveButtonKeytip } from '../../utilities';
import { AppBaseButton } from '../AppBaseButton';
import { getLabelButtonProps } from '../AppBaseButton/getLabelButtonProps';
import { AppButtonInternalProps } from './AppButton.Props';

export class AppButton extends React.PureComponent<AppButtonInternalProps, {}> {
  private baseButton = React.createRef<AppBaseButton>();
  private tooltipHost: AppTooltipHost | undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public render() {
    return (
      <AppBaseButton
        ref={this.baseButton}
        styles={this.props.styles}
        onClick={this.onExecute}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onKeyDown={this.props.onKeyDown}
        onKeyUp={this.props.onKeyUp}
        onMouseDown={this.props.onMouseDown}
        disabled={this.props.disabled}
        takeFocusOnClick={this.props.takeFocusOnClick}
        iconProps={iconNameToIconProps(
          this.props.icon,
          this.props.iconColor,
          undefined /** hideIcon */,
          this.props.flipIconInRtl
        )}
        {...getLabelButtonProps(this.props.label, this.props.hideLabel)}
        data-automation-type={this.props.dataAutomationType || 'AppButton'}
        data-unique-id={dataUniqueId(this.props)}
        role={this.props.role}
        keytipProps={deriveButtonKeytip(this.props.keytipProps, this.keytipExecute)}
        title={this.props.title}
        ariaLabel={this.props.fullDescriptionLabel || this.props.label}
        ariaDescription={
          this.props.ariaDescription
            ? this.props.customTooltip
              ? this.props.ariaDescription.trim() + ' ' + this.props.customTooltip.trim()
              : this.props.ariaDescription.trim()
            : this.props.customTooltip
        }
        aria-expanded={this.props.ariaExpanded}
        // Internally creates a hidden DOM element with this string, and sets aria-describedby
        onRenderText={this.props.onRenderText}
        onRenderChildren={this.props.onRenderChildren}
        lang={this.props.lang}
        id={this.props.noDomId ? undefined : this.props.id}
        data-nav={this.props.dataNav}
        className={this.props.className}
        aria-keyshortcuts={this.props.keyShortcuts}
      />
    );
  }

  public componentDidMount() {
    this.initializeTooltipHost();
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
    }
  }

  public componentDidUpdate(prevProps: AppButtonInternalProps) {
    if (
      prevProps.customTooltip !== this.props.customTooltip ||
      prevProps.label !== this.props.label ||
      prevProps.hideLabel !== this.props.hideLabel
    ) {
      if (this.tooltipHost) {
        this.tooltipHost.updateTooltip(
          getTooltipText(this.props.hideLabel, this.props.label, this.props.customTooltip)
        );
      } else {
        this.initializeTooltipHost();
      }
    }
  }

  private initializeTooltipHost() {
    if (this.context.setTooltipElement && this.context.updateTooltipsHidden) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        refElement: this.baseButton,
        controlId: this.props.id,
        controlLabel: getTooltipText(this.props.hideLabel, this.props.label, this.props.customTooltip),
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: !!checkExperiment(this.context, ribbonExperiments.delayRenderTooltipsIsEnabled),
        targetOverflowSetItem: !!checkExperiment(this.context, ribbonExperiments.tooltipsTargetOverflowSetItemEnabled),
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  }

  public focus() {
    this.baseButton && this.baseButton.current && this.baseButton.current.focus();
  }

  public removeToolTip() {
    this.tooltipHost?.setTooltipUndefined();
  }

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget, this.executeFromKeytip);
  };

  private onExecute = (ev: React.MouseEvent<Button>) => {
    if (this.props.onExecute) {
      const { id, location, shouldTakeFocus } = this.props;
      this.props.onExecute({
        id,
        location,
        inputMethod: this.baseButton?.current?.getInputMethod(ev),
        shouldTakeFocus,
        triggerTime: ev && ev.timeStamp,
      });
    }
  };

  private executeFromKeytip = () => {
    if (this.props.onExecute) {
      const { id, location } = this.props;
      this.props.onExecute({ id, location, inputMethod: InputMethod.Keyboard });
    }
  };

  private onFocus = () => {
    this.props?.onFocus?.();
  };

  private onBlur = () => {
    this.props?.onBlur?.();
  };
}
